import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { CheckIcon, ChevronDownIcon } from '@heroicons/react/outline';
import clsx from 'clsx';

export interface DropdownOption<T = string> {
  value: T;
  label: string;
}

interface DropdownProps<T = string> {
  options: DropdownOption<T>[];
  value: T;
  onChange: (value: T) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  ariaLabel?: string;
  dataTestId: string;
}

export const Dropdown = <T = string,>({
  options,
  value,
  onChange,
  placeholder = 'Select...',
  className,
  disabled = false,
  ariaLabel,
  dataTestId,
}: DropdownProps<T>) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find((option) => option.value === value);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (disabled) return;

    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        setIsOpen(!isOpen);
        break;
      case 'Escape':
        setIsOpen(false);
        break;
      case 'ArrowDown':
        event.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        }
        break;
    }
  };

  const handleOptionSelect = (optionValue: T) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  return (
    <div className={clsx('relative', className)} ref={dropdownRef}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        className={clsx(
          'flex w-full items-center justify-between rounded-md border border-gray-300 bg-white px-[12px] py-[6px] text-left',
          'shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]',
          'focus:border-blue-600 focus:ring-1 focus:ring-blue-600 focus:outline-none',
          disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:bg-gray-50',
        )}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label={ariaLabel}
        data-testid={dataTestId}
      >
        <span className="font-inter truncate text-xs leading-4 font-medium text-gray-600" title={selectedOption?.label}>
          {selectedOption?.label || placeholder}
        </span>
        <ChevronDownIcon
          className={clsx('ml-2 h-4 w-4 shrink-0 text-gray-500 transition-transform', isOpen && 'rotate-180')}
        />
      </button>

      {isOpen && (
        <div
          className={clsx(
            'absolute z-50 mt-1 w-full min-w-44 rounded-md border border-gray-300 bg-white py-1',
            'shadow-[0px_10px_15px_-3px_rgba(0,0,0,0.1),0px_4px_6px_-2px_rgba(0,0,0,0.05),0px_0px_0px_1px_rgba(0,0,0,0.05)]',
          )}
        >
          <ul className="max-h-60 overflow-auto" role="listbox">
            {options.map((option) => {
              const isSelected = option.value === value;
              return (
                <li key={String(option.value)} role="option">
                  <button
                    type="button"
                    onClick={() => handleOptionSelect(option.value)}
                    className={clsx(
                      'font-inter flex w-full items-center justify-between px-3 py-1.5 text-left text-xs leading-5',
                      'hover:bg-gray-50 focus:bg-gray-50 focus:outline-none',
                      isSelected ? 'font-semibold text-gray-900' : 'font-normal text-gray-900',
                    )}
                  >
                    <span className="truncate" title={option.label}>
                      {option.label}
                    </span>
                    {isSelected && <CheckIcon className="text-primary-500 ml-2 h-5 w-5 shrink-0" />}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};
